import React, { useEffect, useState } from 'react';
import notify from '../../utils/notifier';
import LocationsService from '../../services/LocationsService';

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  InputAdornment,
  Typography,
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PaginationComponent from './../PaginationComponent';
import DownloadIcon from '@mui/icons-material/Download';
import { IconButton } from '@mui/material';


const downloadCities = async (countryIso2 = 'IN', stateId = '') => {
    const query = {};
    if (stateId) query._stateId = stateId;
    else  query._countryIso2 = countryIso2;

    try {
      if(query._format === undefined || !query._format){
        query._format = 'CSV'
      }

      const response = await LocationsService.downloadCitiesData(query);
      console.log('Download response:', response.data);
      const link = document.createElement('a');
      link.href = URL.createObjectURL(response.data);
      
      const timestamp = new Date().toLocaleString('en-GB').replace(/[/,: ]/g, '-');
      const fileName = `cities_${timestamp}.csv`;
      
      link.download = fileName; 
      link.click();
      setTimeout(() => {
        notify({
          type: 'success',
          message: 'File downloaded successfully.'
        });
      }, 50); 
    } catch (error) {
      console.error('Failed to download file:', error);
      notify({
        type: 'error',
        message: 'Something went wrong while downloading the file.'
      });
    }
};

const fetchCities = async (countryIso2 = 'IN', stateId = '') => {
  let output = [];
  try {
    const query = {};
    if (stateId) query._stateId = stateId;
    else  query._countryIso2 = countryIso2;
    const response = await LocationsService.getCitiesData(query);
    response.data.forEach(city => {
      output.push(city);
    });
  } catch (error) {
    notify({
      type: 'error',
      message: 'Internal Server Error.'
    });
  }
  return output;
};

const fetchCountries = async () => {
  let output = [];
  try {
    const response = await LocationsService.getCountriesData();
    response.data.forEach(country => {
      output.push(country);
    });
  } catch (error) {
    notify({
      type: 'error',
      message: 'Internal Server Error.'
    });
  }
  return output;
};

const fetchStates = async (countryIso2 = 'IN') => {
  let output = [];
  try {
    const query = { _countryIso2: countryIso2 };
    const response = await LocationsService.getStatesData(query);
    response.data.forEach(state => {
      output.push(state);
    });
  } catch (error) {
    notify({
      type: 'error',
      message: 'Internal Server Error.'
    });
  }
  return output;
};

const columns = [
  { id: 'id', label: 'ID' },
  { id: 'name', label: 'City Name' },
  { id: 'cityCode', label: 'City Code' },
  { id: 'stateName', label: 'State Name' },
  { id: 'stateIso2', label: 'State ISO2' },
  { id: 'stateCode', label: 'State Code' },
  { id: 'countryName', label: 'Country Name' },
  { id: 'countryIso2', label: 'Country ISO2' },
];

const StaticCitiesTable = () => {
  const [cities, setCities] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [search, setSearch] = useState('');
  const [filtered, setFiltered] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [selectedCountry, setSelectedCountry] = useState('IN'); // Default to India
  const [selectedState, setSelectedState] = useState(''); // Optional, default empty
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchCountries().then(setCountries);
  }, []);

  useEffect(() => {
    if (selectedCountry) {
      setLoading(true);
      fetchStates(selectedCountry).then(setStates);
      fetchCities(selectedCountry, selectedState).then((data) => {
        setCities(data);
        setLoading(false);
      });
    }
  }, [selectedCountry, selectedState]);

  useEffect(() => {
    setFiltered(
      cities.filter(
        c =>
          (c.name && c.name.toLowerCase().includes(search.toLowerCase())) ||
          (c.cityCode && c.cityCode.toLowerCase().includes(search.toLowerCase())) ||
          (c.stateName && c.stateName.toLowerCase().includes(search.toLowerCase())) ||
          (c.stateIso2 && c.stateIso2.toLowerCase().includes(search.toLowerCase())) ||
          (c.stateCode && c.stateCode.toLowerCase().includes(search.toLowerCase())) ||
          (c.countryName && c.countryName.toLowerCase().includes(search.toLowerCase())) ||
          (c.countryIso2 && c.countryIso2.toLowerCase().includes(search.toLowerCase())) ||
          (c.id && c.id.toLowerCase().includes(search.toLowerCase()))
      )
    );
    setPage(0); // Reset to first page when searching
  }, [search, cities]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCountryChange = (event) => {
    setSelectedCountry(event.target.value);
    setSelectedState(''); // Reset state filter
    setSearch(''); // Clear search when country changes
  };

  const handleStateChange = (event) => {
    setSelectedState(event.target.value);
    setSearch(''); // Clear search when state changes
  };

  // Calculate paginated data
  const paginatedData = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box sx={{ p: 3, backgroundColor: '#F2F2F2', minHeight: 'calc(100vh - 144px)' }}>
      <Paper sx={{ p: 3, backgroundColor: '#FFFFFF' }}>
        <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
          <Grid item xs={12} sm={3}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#298BC1' }}>
              Cities
            </Typography>
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Country</InputLabel>
              <Select
                value={selectedCountry}
                label="Country"
                onChange={handleCountryChange}
              >
                {countries.map((country) => (
                  <MenuItem key={country.iso2} value={country.iso2}>
                    {country.name} ({country.iso2})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>{selectedState == ''? '' : 'State'}</InputLabel>
              <Select
                value={selectedState}
                label="State"
                onChange={handleStateChange}
                displayEmpty
              >
                <MenuItem value="">
                  <em>All State</em>
                </MenuItem>
                {states.map((state) => (
                  <MenuItem key={state.id} value={state.id}>
                    {state.name} ({state.stateIso2})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                variant="outlined"
                placeholder="Search by city, code, state, country, ID..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                size="small"
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <IconButton
                  onClick={() => downloadCities(selectedCountry, selectedState)}   
                  sx={{
                    backgroundColor: '#298BC1',
                    color: 'white',
                    '&:hover': { backgroundColor: '#2477a9' },
                    borderRadius: '6px',
                    p: 1.2
                  }}
                >
                <DownloadIcon />
              </IconButton>
            </Box>            
          </Grid>
        </Grid>

        <TableContainer 
          component={Paper} 
          sx={{ 
            boxShadow: 'none', 
            border: '1px solid #e0e0e0',
            maxHeight: 'calc(100vh - 300px)',
            overflow: 'auto'
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                {columns.map(col => (
                  <TableCell 
                    key={col.id} 
                    sx={{ 
                      fontWeight: 'bold', 
                      color: '#333',
                      borderBottom: '2px solid #e0e0e0',
                      backgroundColor: '#f5f5f5'
                    }}
                  >
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center" sx={{ py: 4, color: '#666' }}>
                    Loading cities...
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  {paginatedData.map(row => (
                    <TableRow 
                      key={row.id}
                      sx={{ 
                        '&:hover': { backgroundColor: '#f8f9fa' },
                        '&:nth-of-type(odd)': { backgroundColor: '#fafafa' }
                      }}
                    >
                      {columns.map(col => (
                        <TableCell 
                          key={col.id}
                          sx={{ 
                            borderBottom: '1px solid #e0e0e0',
                            color: '#333'
                          }}
                        >
                          {row[col.id]}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                  {paginatedData.length === 0 && !loading && (
                    <TableRow>
                      <TableCell colSpan={columns.length} align="center" sx={{ py: 4, color: '#666' }}>
                        No cities found.
                      </TableCell>
                    </TableRow>
                  )}
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ mt: 2 }}>
          <PaginationComponent
            queryCount={filtered.length}
            page={page}
            handleChangePage={handleChangePage}
            rowsPerPage={rowsPerPage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
          />
        </Box>
      </Paper>
    </Box>
  );
};

export default StaticCitiesTable; 